export type MessageFolder = 'inbox' | 'sent' | 'draft' | 'deleted';
export type MessageDirection = 'inbound' | 'outbound';
export type MessageStatus = 'pending' | 'sent' | 'delivered' | 'received' | 'failed';

export class UpdoxDirectMessage {
  id: number;
  practice_id: number;
  updox_user_id: number;
  patient_id: number;
  updox_message_id: string = '';
  direction: MessageDirection;
  from_address: string = '';
  to_address: string = '';
  subject: string = '';
  body_text: string = '';
  body_html: string = '';
  folder: MessageFolder = 'inbox';
  status: MessageStatus = 'pending';
  handled_by_user_id: number;
  read: boolean = false;
  added_to_medical_chart: boolean = false;
  reply_to_message_id: number = null;
  has_attachments: boolean = false;
  created_at: string;
  updated_at: string;

  get sender_initial(): string {
    const src = this.from_address || '';
    return src.charAt(0).toUpperCase() || '?';
  }

  get display_sender(): string {
    return this.from_address || 'Unknown';
  }

  // For message list rows — drafts are outbound so show recipient, not sender.
  get list_sender(): string {
    if (this.folder === 'draft') {
      return this.to_address || 'Unknown';
    }
    return this.display_sender;
  }

  get list_initial(): string {
    if (this.folder === 'draft') {
      const src = this.to_address || '';
      return src.charAt(0).toUpperCase() || '?';
    }
    return this.sender_initial;
  }

  // Drafts: show last-saved time; all others: show received/sent time.
  get list_date(): string {
    return this.folder === 'draft' ? (this.updated_at || this.created_at) : this.created_at;
  }

  get preview(): string {
    return this.body_text ? this.body_text.substring(0, 90).replace(/\n/g, ' ') : '';
  }

  get display_subject(): string {
    return this.subject || '(No Subject)';
  }

  load_from_json(json: any): UpdoxDirectMessage {
    if (!json) { return this; }
    this.id = json.id;
    this.practice_id = json.practice_id;
    this.updox_user_id = json.updox_user_id;
    this.patient_id = json.patient_id;
    this.updox_message_id = json.updox_message_id || '';
    this.direction = json.direction;
    this.from_address = json.from_address || '';
    this.to_address = json.to_address || '';
    this.subject = json.subject || '';
    this.body_text = json.body_text || '';
    this.body_html = json.body_html || '';
    this.folder = json.folder || 'inbox';
    this.status = json.status || 'pending';
    this.handled_by_user_id = json.handled_by_user_id;
    this.read = json.read !== undefined ? json.read : false;
    this.added_to_medical_chart = json.added_to_medical_chart || false;
    this.reply_to_message_id = json.reply_to_message_id || null;
    this.has_attachments = json.has_attachments || false;
    this.created_at = json.created_at;
    this.updated_at = json.updated_at;
    return this;
  }

  to_send_params() {
    return {
      direct_message: {
        to_address:             this.to_address,
        subject:                this.subject,
        body_text:              this.body_text,
        body_html:              this.body_html,
        practice_id:            this.practice_id,
        patient_id:             this.patient_id || null,
        reply_to_message_id:    this.reply_to_message_id || null,
      }
    };
  }
}

export interface ComposeDraft {
  localId: number;
  draftId?: number;
  to_address: string;
  subject: string;
  body_text: string;
  patient_id?: number;
  reply_to_message_id?: number;
  minimized: boolean;
  replyTo?: UpdoxDirectMessage;
}
